import React, { useState, useEffect } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import { Mail, Globe, Check, Loader2, Eye, EyeOff, Lock, Palette, Info } from "lucide-react";
import "react-toastify/dist/ReactToastify.css";
import chatProIcon from "../assets/chatpro.svg";

const PRESETS = [
  { color: '#16a34a', label: 'Green'   },
  { color: '#2563eb', label: 'Blue'    },
  { color: '#7c3aed', label: 'Violet'  },
  { color: '#dc2626', label: 'Red'     },
  { color: '#ea580c', label: 'Orange'  },
  { color: '#0891b2', label: 'Cyan'    },
  { color: '#be185d', label: 'Pink'    },
  { color: '#374151', label: 'Slate'   },
];

const inputCls =
  "w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 transition";

const SectionCard = ({ icon: Icon, title, description, children }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
    <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 bg-gray-50/60">
      <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-indigo-600" />
      </div>
      <div>
        <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
        {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
      </div>
    </div>
    <div className="px-6 py-5">{children}</div>
  </div>
);

const Profile = () => {
  const [password, setPassword] = useState("");
  const [retypePassword, setRetypePassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [contactMail, setContactMail] = useState("");
  const [contactLink, setContactLink] = useState("");
  const [savedMail, setSavedMail] = useState("");
  const [savedContact, setSavedContact] = useState("");
  const [mailLoading, setMailLoading] = useState(false);
  const [linkLoading, setLinkLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [themeColor, setThemeColor] = useState("#16a34a");
  const [savedThemeColor, setSavedThemeColor] = useState("#16a34a");
  const [themeLoading, setThemeLoading] = useState(false);
  const orgName = sessionStorage.getItem("organizationName");
  const organizationId = sessionStorage.getItem(`${orgName}_organizationId`);
  const role = sessionStorage.getItem(`${orgName}_role`);

  // Fetch existing contact info on mount
  useEffect(() => {
    if (!organizationId) {
      setLoading(false);
      return;
    }

    const fetchContactInfo = async () => {
      try {
        const [mailRes, linkRes, colorRes] = await Promise.all([
          axios
            .get(
              `${process.env.REACT_APP_API_URL}/users/getContactUsMail/${organizationId}`
            )
            .catch(() => null),
          axios
            .get(
              `${process.env.REACT_APP_API_URL}/users/getContactUsLink/${organizationId}`
            )
            .catch(() => null),
          axios
            .get(
              `${process.env.REACT_APP_API_URL}/users/getThemeColor/${organizationId}`
            )
            .catch(() => null),
        ]);

        if (mailRes?.data?.contact_us_email) {
          setSavedMail(mailRes.data.contact_us_email);
        }
        if (linkRes?.data?.contact_us_page) {
          setSavedContact(linkRes.data.contact_us_page);
        }
        if (colorRes?.data?.themeColor) {
          setSavedThemeColor(colorRes.data.themeColor);
          setThemeColor(colorRes.data.themeColor);
        }
      } catch (error) {
        console.error("Error fetching contact info:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchContactInfo();
  }, [organizationId]);

  const handleSaveThemeColor = async () => {
    if (!organizationId) return;
    setThemeLoading(true);
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/updateThemeColor`, {
        organization_id: organizationId,
        themeColor,
      });
      setSavedThemeColor(themeColor);
      toast.success("Theme color updated successfully!");
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to update theme color.");
    } finally {
      setThemeLoading(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (!organizationId) return toast.error("Organization ID not found.");
    if (!password.trim()) return toast.error("Please enter a new password.");
    if (password !== retypePassword) return toast.error("Passwords do not match.");
    setPasswordLoading(true);
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/updatePassword`, {
        password,
        organization_id: organizationId,
      });
      setPassword("");
      setRetypePassword("");
      toast.success("Password updated successfully!");
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to update password.");
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleAddMail = async () => {
    if (!contactMail.trim()) return toast.error("Please enter a valid email.");
    if (!organizationId) return toast.error("Organization ID not found.");
    setMailLoading(true);
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/addContactUsMail`, {
        contactMail,
        organization_id: organizationId,
      });
      setSavedMail(contactMail);
      setContactMail("");
      toast.success("Contact email updated successfully!");
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to update contact email.");
    } finally {
      setMailLoading(false);
    }
  };

  const handleAddContact = async () => {
    if (!contactLink.trim()) return toast.error("Please enter a valid website link.");
    if (!organizationId) return toast.error("Organization ID not found.");
    setLinkLoading(true);
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/addContactUsLink`, {
        contactLink,
        organization_id: organizationId,
      });
      setSavedContact(contactLink);
      setContactLink("");
      toast.success("Contact link updated successfully!");
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to update contact link.");
    } finally {
      setLinkLoading(false);
    }
  };

  const passwordsMatch = retypePassword.length > 0 && password === retypePassword;
  const passwordMismatch = retypePassword.length > 0 && password !== retypePassword;

  return (
    <div className="min-h-screen bg-gray-50/70 p-6">
      <ToastContainer autoClose={3000} position="top-right" />

      {/* Page header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-sm text-gray-500 mt-1">Manage your account and widget preferences.</p>
      </div>

      <div className="max-w-2xl space-y-5">

        {/* ── Change Password ── */}
        <SectionCard
          icon={Lock}
          title="Change Password"
          description="Update your account login password"
        >
          <div className="space-y-4">
            {/* New password */}
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  className={inputCls + " pr-10"}
                  placeholder="Enter new password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Confirm password */}
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type={showConfirm ? "text" : "password"}
                  className={
                    inputCls +
                    " pr-10 " +
                    (passwordMismatch
                      ? "border-red-300 focus:ring-red-200 focus:border-red-400"
                      : passwordsMatch
                      ? "border-green-300 focus:ring-green-200 focus:border-green-400"
                      : "")
                  }
                  placeholder="Re-type new password"
                  value={retypePassword}
                  onChange={(e) => setRetypePassword(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
                  tabIndex={-1}
                >
                  {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {passwordMismatch && (
                <p className="text-xs text-red-500 mt-1">Passwords do not match.</p>
              )}
              {passwordsMatch && (
                <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                  <Check className="w-3 h-3" /> Passwords match
                </p>
              )}
            </div>

            <button
              onClick={handleUpdatePassword}
              disabled={passwordLoading || !password || passwordMismatch}
              className="w-full flex items-center justify-center gap-2 py-2.5 bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-sm rounded-xl transition"
            >
              {passwordLoading && <Loader2 className="w-4 h-4 animate-spin" />}
              {passwordLoading ? "Updating…" : "Update Password"}
            </button>
          </div>
        </SectionCard>

        {/* ── Contact Info ── */}
        {role !== "superadmin" && role !== "agent" && (
          <SectionCard
            icon={Mail}
            title="Contact Us Information"
            description="These details appear on your client-facing chat widget"
          >
            {loading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="w-5 h-5 animate-spin text-gray-300" />
              </div>
            ) : (
              <div className="space-y-5">
                {/* Contact email */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                    Support Email
                  </label>
                  {savedMail && (
                    <div className="flex items-center gap-2 mb-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-700">
                      <Check className="w-3.5 h-3.5 flex-shrink-0" />
                      <Mail className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="truncate font-medium">{savedMail}</span>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input
                      type="email"
                      className={inputCls}
                      placeholder={savedMail ? "Enter new email to update" : "info@example.com"}
                      value={contactMail}
                      onChange={(e) => setContactMail(e.target.value)}
                    />
                    <button
                      onClick={handleAddMail}
                      disabled={mailLoading || !contactMail.trim()}
                      className="flex items-center gap-1.5 px-4 py-2.5 bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-sm rounded-xl transition whitespace-nowrap"
                    >
                      {mailLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                      {savedMail ? "Update" : "Save"}
                    </button>
                  </div>
                </div>

                {/* Contact link */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                    Support Website
                  </label>
                  {savedContact && (
                    <div className="flex items-center gap-2 mb-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-700">
                      <Check className="w-3.5 h-3.5 flex-shrink-0" />
                      <Globe className="w-3.5 h-3.5 flex-shrink-0" />
                      <a
                        href={savedContact}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-emerald-700 underline truncate font-medium"
                      >
                        {savedContact}
                      </a>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input
                      type="url"
                      className={inputCls}
                      placeholder={savedContact ? "Enter new link to update" : "https://example.com/contact"}
                      value={contactLink}
                      onChange={(e) => setContactLink(e.target.value)}
                    />
                    <button
                      onClick={handleAddContact}
                      disabled={linkLoading || !contactLink.trim()}
                      className="flex items-center gap-1.5 px-4 py-2.5 bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-sm rounded-xl transition whitespace-nowrap"
                    >
                      {linkLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                      {savedContact ? "Update" : "Save"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </SectionCard>
        )}

        {/* ── Widget Theme Color ── */}
        {role !== "superadmin" && role !== "agent" && (
          <SectionCard
            icon={Palette}
            title="Widget Theme Color"
            description="Buttons, bubbles and icons all update to match this color"
          >
            {/* Preset swatches */}
            <div className="grid grid-cols-8 gap-2 mb-5">
              {PRESETS.map(({ color, label }) => (
                <button
                  key={color}
                  onClick={() => setThemeColor(color)}
                  title={label}
                  className="group relative aspect-square rounded-xl border-2 transition-all hover:scale-105 focus:outline-none"
                  style={{
                    background: color,
                    borderColor: themeColor === color ? '#1e293b' : 'transparent',
                    boxShadow: themeColor === color ? `0 0 0 2px white, 0 0 0 4px ${color}` : 'none',
                  }}
                >
                  {themeColor === color && (
                    <Check className="absolute inset-0 m-auto w-3.5 h-3.5 text-white drop-shadow" />
                  )}
                </button>
              ))}
            </div>

            {/* Custom picker row */}
            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-2xl border border-gray-100 mb-4">
              <div className="relative flex-shrink-0">
                <input
                  type="color"
                  value={themeColor}
                  onChange={(e) => setThemeColor(e.target.value)}
                  className="w-11 h-11 rounded-xl cursor-pointer border-0 p-0.5 bg-transparent"
                  title="Pick a custom color"
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Custom color</p>
                <div className="flex items-center gap-2">
                  <span
                    className="w-5 h-5 rounded-md border border-black/10 flex-shrink-0"
                    style={{ background: themeColor }}
                  />
                  <code className="text-sm text-gray-700 font-mono">{themeColor.toUpperCase()}</code>
                </div>
              </div>
              {savedThemeColor !== themeColor && (
                <div className="flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 flex-shrink-0">
                  <Info className="w-3 h-3" />
                  Unsaved
                </div>
              )}
            </div>

            {/* Live preview */}
            <div className="mb-5">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Live preview</p>
              <div className="flex gap-3 items-center">
                <div
                  className="flex-1 h-10 rounded-xl flex items-center justify-center text-white text-sm font-semibold shadow-sm"
                  style={{ background: themeColor }}
                >
                  Button
                </div>
                <div
                  className="px-4 py-2.5 rounded-2xl rounded-br-sm text-white text-sm font-medium shadow-sm"
                  style={{ background: themeColor }}
                >
                  Chat bubble
                </div>
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center shadow-sm flex-shrink-0"
                  style={{ background: themeColor }}
                >
                  <img
                    src={chatProIcon}
                    alt="chat"
                    width={25}
                    height={25}
                    style={{ filter: 'brightness(0) invert(1)' }}
                  />
                </div>
              </div>
            </div>

            <button
              onClick={handleSaveThemeColor}
              disabled={themeLoading || themeColor === savedThemeColor}
              className="w-full flex items-center justify-center gap-2 py-2.5 text-white font-semibold text-sm rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              style={{
                background: themeColor === savedThemeColor ? '#9ca3af' : themeColor,
              }}
            >
              {themeLoading && <Loader2 className="w-4 h-4 animate-spin" />}
              {themeLoading ? "Saving…" : themeColor === savedThemeColor ? "Color saved" : "Save Theme Color"}
            </button>
          </SectionCard>
        )}

      </div>
    </div>
  );
};

export default Profile;
